import { useId, useRef, useState, type KeyboardEvent, type SyntheticEvent } from "react";
import type { BuyerLeadSummaryDto, BuyerSpecialistCandidateDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { InputModal } from "@/components/ui/PromptModals";
import { Select } from "@/components/ui/Select";
import { cn } from "@/design-system/lib/utils";
import {
  useAssignBuyer,
  useHandOffRoster,
  useSpecialistCandidates,
  useUnassignBuyer,
} from "@/hooks/buyers/useBuyerAssignments";

/**
 * Assignment from anywhere — the specialist picker, and the list cell that
 * changes an owner in place.
 *
 * <p>Two components, one rule: <b>the server decides who is offerable and who
 * may change an owner; nothing here re-derives either.</b> The candidate list is
 * `GET /buyer-assignments/candidates` (`BuyerSpecialistCandidatesDto`), scoped
 * server-side to the teams the caller dispatches — or, for an owner, to their
 * own team — and the row-level `canReassign` / `canUnassign` are
 * `BuyerReassignmentRule`'s answers, projected onto the row.
 * `SpecialistQuickPick` reads the caller's hand-off roster
 * (`GET /buyer-assignments/hand-off-roster`) instead of `candidates` per buyer:
 * it is the same people for every buyer the caller may reassign, so a page of
 * rows makes ONE request. `SpecialistQuickPick` renders a CONTROL only
 * when that answer is true and plain text otherwise, which is the Table & Tile
 * rule that a control which cannot do what it appears to do is worse than no
 * control. The server enforces the same rule regardless, so a false `true`
 * here costs a 400 rendered inline, never a silent write.</p>
 *
 * <p>The Buyer CRM authorises at TWO gates — the endpoint's policy and the
 * row's ownership check inside the service — and this surface is built to
 * clear neither on the client's say-so: it offers exactly what the candidates
 * read returned, and it shows the server's refusal verbatim when one comes.</p>
 */

const NO_SCOPE_CAPTION = "You are not on a buyer team, so there is nobody to assign to.";
const EMPTY_ROSTER_CAPTION = "No consultants on your team hold a buyer role.";
const LOAD_FAILED_CAPTION = "Couldn't load who you can assign to.";

/** "Name · N active", plus the team when the roster spans more than one. */
function candidateLabel(c: BuyerSpecialistCandidateDto, showTeam: boolean): string {
  const parts = [c.displayName?.trim() || c.specialistUserId, `${c.activeBuyers} active`];
  if (showTeam && c.buyerTeamName) parts.push(c.buyerTeamName);
  return parts.join(" · ");
}

export interface SpecialistCandidateSelectProps {
  /** The buyer whose owner is being chosen, or null for a buyer about to be created. */
  buyerLeadId: string | null;
  value: string | null;
  onChange: (specialistUserId: string | null) => void;
  disabled?: boolean;
  /** Offer an "Unassigned (queue)" option that maps to null. Default true. */
  includeUnassignedOption?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Don't fetch (e.g. the caller lacks the permission). Default true. */
  enabled?: boolean;
  /**
   * Where the offerable people come from. `"buyer"` (default): the per-buyer
   * `candidates` read for `buyerLeadId` (or a buyer about to be created).
   * `"caller"`: the caller's hand-off roster — one read shared by every row on
   * a page, for a buyer the host already knows the caller may reassign.
   */
  source?: "buyer" | "caller";
  /**
   * ADDITIVE and optional — not in the agreed contract, added so the list cell
   * can sit in a table row without a second component. `"sm"` is the row-height
   * variant; the default is the kit's own control height.
   */
  size?: "sm" | "md";
}

/**
 * A controlled `<select>` over the caller's assignment candidates, grouped
 * Specialists (F2) then Managers (F1). No mutation lives here: it reports a
 * choice and nothing else, so the create forms and the list cell can each
 * decide what a choice means.
 *
 * <p>🔴 <b>Never a silently empty list.</b> A caller on no buyer team and a
 * caller whose team genuinely holds nobody both get zero candidates, and they
 * need opposite remedies — so the select is disabled with a caption that says
 * WHICH, read from the server's `scopeKind` rather than inferred from the
 * count. A failed read gets the same treatment: an empty `<select>` after a
 * 500 reads as "nobody", which is a false statement.</p>
 */
export function SpecialistCandidateSelect({
  buyerLeadId,
  value,
  onChange,
  disabled,
  includeUnassignedOption = true,
  id,
  "aria-label": ariaLabel,
  enabled = true,
  size = "md",
  source = "buyer",
}: SpecialistCandidateSelectProps) {
  const captionId = useId();
  const perBuyer = useSpecialistCandidates(buyerLeadId, { enabled: enabled && source === "buyer" });
  const perCaller = useHandOffRoster({ enabled: enabled && source === "caller" });
  const query = source === "caller" ? perCaller : perBuyer;
  // A disabled query reports `pending` forever, so "loading" has to be gated on
  // `enabled` or a caller without the permission sees "Loading…" for life.
  const loading = enabled && query.isPending;
  const failed = enabled && query.isError;
  const data = enabled ? query.data : undefined;

  const list = data?.candidates ?? [];
  const noScope = data?.scopeKind === "NoScope";
  const emptyRoster = data != null && !noScope && list.length === 0;
  const specialists = list.filter((c) => c.buyerRoleCode === "F2");
  const managers = list.filter((c) => c.buyerRoleCode === "F1");
  // The team rides the label only when it disambiguates — one team's roster
  // repeating its own name on every line is noise.
  const showTeam = new Set(list.map((c) => c.buyerTeamName ?? "")).size > 1;
  const valueIsOffered = value != null && list.some((c) => c.specialistUserId === value);

  const caption = noScope
    ? NO_SCOPE_CAPTION
    : emptyRoster
      ? EMPTY_ROSTER_CAPTION
      : failed
        ? LOAD_FAILED_CAPTION
        : null;

  return (
    <div className="flex max-w-full flex-col gap-space-1">
      <Select
        id={id}
        aria-label={ariaLabel}
        aria-describedby={caption ? captionId : undefined}
        disabled={disabled || loading || !enabled || noScope || emptyRoster || failed}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={cn("w-auto max-w-full text-small", size === "sm" && "h-9")}
      >
        {loading ? (
          // Carries the current value so the control reads "Loading…" rather
          // than blank — a select whose value matches no option renders empty.
          <option value={value ?? ""}>Loading…</option>
        ) : (
          <>
            {includeUnassignedOption ? (
              <option value="">Unassigned (queue)</option>
            ) : value == null ? (
              // The CURRENT state, shown but not offered: `disabled` keeps it
              // out of the choices while letting it stay the selected one.
              <option value="" disabled>
                Unassigned
              </option>
            ) : null}
            {value != null && !valueIsOffered && (
              // The owner is real but not on the roster the caller may assign
              // to (a cross-team holder, D2). Shown so the control tells the
              // truth about who holds the buyer; not offered as a destination.
              <option value={value} disabled>
                Current owner
              </option>
            )}
            {specialists.length > 0 && (
              <optgroup label="Consultants">
                {specialists.map((c) => (
                  <option key={c.specialistUserId} value={c.specialistUserId}>
                    {candidateLabel(c, showTeam)}
                  </option>
                ))}
              </optgroup>
            )}
            {managers.length > 0 && (
              <optgroup label="Managers">
                {managers.map((c) => (
                  <option key={c.specialistUserId} value={c.specialistUserId}>
                    {candidateLabel(c, showTeam)}
                  </option>
                ))}
              </optgroup>
            )}
          </>
        )}
      </Select>
      {caption && (
        <span id={captionId} className="text-caption text-gray-mid">
          {caption}
        </span>
      )}
    </div>
  );
}

export interface SpecialistQuickPickProps {
  buyerLeadId: string;
  currentSpecialistUserId: string | null | undefined;
  currentSpecialistName: string | null | undefined;
  /** Server-resolved. False renders plain text (the name, or "Unassigned"). */
  canReassign: boolean;
  /** Server-resolved, from the same row (`canUnassign`): whether "Unassigned
   *  (queue)" is offered. Required so a host cannot silently withhold it. */
  canUnassign: boolean;
  /** Called after a successful assign/unassign with the row the server returned. */
  onChanged?: (row: BuyerLeadSummaryDto) => void;
  size?: "sm" | "md";
  /**
   * ADDITIVE and optional — not in the agreed contract. Names the buyer in the
   * reason prompt ("Reassign Dana Whitfield from …"); a host without it gets
   * "this buyer". The list has the name on the row and passes it.
   */
  buyerDisplayName?: string;
}

/**
 * The specialist cell as a control: the current owner preselected, a
 * different choice opens a reason prompt, and the confirmed move posts to the
 * assign or unassign endpoint.
 *
 * <p>`canReassign` false renders plain text. The name or an italic
 * "Unassigned" — muted prose, never a pill, because an unowned buyer is a
 * normal state of a young book (§6).</p>
 *
 * <p>Nothing here patches the row. A successful move resolves after the list,
 * the queue and the standings have been invalidated (see
 * `useBuyerAssignments`), so the refetched row carries the new owner and this
 * cell simply re-renders from it. On a refusal the select keeps showing the
 * row's owner — the prop never moved — and the server's message renders inline
 * as an alert.</p>
 *
 * <p>🔴 Clicks, and the Enter/Space keydowns a `SubTable` row acts on, are
 * stopped here so choosing an owner never also navigates the row. Only those
 * two keys: Escape must keep bubbling, because the reason prompt's
 * Escape-to-close is a bubble-phase `document` listener and a blanket stop
 * would strand it.</p>
 */
export function SpecialistQuickPick({
  buyerLeadId,
  currentSpecialistUserId,
  currentSpecialistName,
  canReassign,
  canUnassign,
  onChanged,
  size = "md",
  buyerDisplayName,
}: SpecialistQuickPickProps) {
  const current = currentSpecialistUserId ?? null;
  // The same key the select reads — and every other row's quick-pick — so
  // react-query serves ONE request to the whole page.
  const candidates = useHandOffRoster({ enabled: canReassign });
  const assign = useAssignBuyer();
  const unassign = useUnassignBuyer();
  // `undefined` = no prompt open; `null` = returning to the queue; a string =
  // the specialist chosen. Three states, because "no choice" and "chose
  // Unassigned" must not share a value.
  const [pending, setPending] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  // A ref rather than the mutations' `isPending`: two confirms in one tick
  // would both read the pre-render value and post twice.
  const inFlight = useRef(false);

  if (!canReassign) {
    return currentSpecialistName ? (
      <span>{currentSpecialistName}</span>
    ) : (
      <span className="text-gray-mid italic">Unassigned</span>
    );
  }

  const buyer = buyerDisplayName?.trim() || "this buyer";
  const busy = assign.isPending || unassign.isPending;
  const roster = candidates.data?.candidates ?? [];
  const ownerOffRoster =
    current != null && candidates.data != null && !roster.some((c) => c.specialistUserId === current);

  const candidateName = (specialistUserId: string) =>
    roster.find((c) => c.specialistUserId === specialistUserId)?.displayName?.trim() ||
    "the chosen consultant";

  const promptTitle =
    pending === null
      ? `Return ${buyer} to the queue`
      : pending === undefined
        ? ""
        : current
          ? `Reassign ${buyer} from ${currentSpecialistName?.trim() || "the current consultant"} to ${candidateName(pending)}`
          : `Assign ${buyer} to ${candidateName(pending)}`;
  const confirmLabel = pending === null ? "Return to queue" : current ? "Reassign" : "Assign";

  const onPick = (next: string | null) => {
    if (next === current) return;
    setError(null);
    setPending(next);
  };

  const confirm = async (reason: string) => {
    if (pending === undefined || inFlight.current) return;
    const target = pending;
    // The prompt's job ends once the reason is captured; the request's outcome
    // renders in the cell, where the select is disabled until it lands.
    setPending(undefined);
    setError(null);
    inFlight.current = true;
    try {
      const row =
        target === null
          ? await unassign.mutateAsync({ buyerLeadId, reason })
          : await assign.mutateAsync({ buyerLeadId, specialistUserId: target, reason });
      onChanged?.(row);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      inFlight.current = false;
    }
  };

  const stopClick = (e: SyntheticEvent) => e.stopPropagation();
  const stopRowKeys = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };

  return (
    <span className="inline-flex max-w-full flex-col gap-space-1" onClick={stopClick} onKeyDown={stopRowKeys}>
      <SpecialistCandidateSelect
        buyerLeadId={buyerLeadId}
        value={current}
        onChange={onPick}
        disabled={busy}
        source="caller"
        // Returning to the queue is offered only where the server says the
        // caller may — never inferred from holding a permission.
        includeUnassignedOption={canUnassign}
        aria-label={`Consultant for ${buyer}`}
        size={size}
      />
      {ownerOffRoster && (
        <span className="text-caption text-gray-mid">
          Currently {currentSpecialistName?.trim() || "held by someone"} who is not on your roster.
        </span>
      )}
      {error && (
        <span role="alert" className="text-caption text-status-error">
          {error}
        </span>
      )}
      <InputModal
        open={pending !== undefined}
        title={promptTitle}
        label="Reason"
        placeholder="Why this buyer is moving"
        confirmLabel={confirmLabel}
        onSubmit={(reason) => void confirm(reason)}
        onCancel={() => setPending(undefined)}
      />
    </span>
  );
}
